import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, GripVertical, Eye, EyeOff, Lightbulb, Globe } from 'lucide-react';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import ConfirmModal from '../components/common/ConfirmModal';
import FieldFormModal from '../components/fields/FieldFormModal';
import { fieldAPI, eventAPI, attendeeAPI } from '../api/endpoints';
import { useToastContext } from '../context/ToastContext';
import { useEvent } from '../context/EventContext';
import EventSelector from '../components/common/EventSelector';
import { deduplicateFields } from '../utils/fieldHelpers';

const CustomFields = () => {
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const { selectedEvent, setSelectedEvent } = useEvent();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [draggedField, setDraggedField] = useState(null);

  // Fetch events with attendee counts
  const { data: eventsData } = useQuery({
    queryKey: ['events-with-counts'],
    queryFn: async () => {
      const response = await eventAPI.getAll({});
      const events = response.data.events || [];
      return { events };
    },
  });

  // Fetch fields for selected event
  const { data: fieldsData, isLoading } = useQuery({
    queryKey: ['fields', selectedEvent],
    queryFn: async () => {
      if (!selectedEvent) return { data: [] };
      const response = await fieldAPI.getAll(selectedEvent);
      return response.data;
    },
    enabled: !!selectedEvent,
  });

  // Create field mutation
  const createMutation = useMutation({
    mutationFn: (data) => fieldAPI.create(selectedEvent, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fields'] });
      queryClient.invalidateQueries({ queryKey: ['registration-config'] });
      toast.success('Field created successfully!');
      setShowFieldModal(false);
      setSelectedField(null);
    },
    onError: (error: any) => {
      console.error('Create field error:', error.response?.data);
      const errorMsg = error.response?.data?.error || 'Failed to create field';
      const details = error.response?.data?.details;
      if (details && Array.isArray(details)) {
        toast.error(`${errorMsg}: ${details.join(', ')}`);
      } else {
        toast.error(errorMsg);
      }
    },
  });

  // Update field mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: any) => fieldAPI.update(selectedEvent, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fields'] });
      queryClient.invalidateQueries({ queryKey: ['registration-config'] });
      toast.success('Field updated successfully!');
      setShowFieldModal(false);
      setSelectedField(null);
    },
    onError: (error: any) => {
      console.error('Update field error:', error.response?.data);
      const errorMsg = error.response?.data?.error || 'Failed to update field';
      const details = error.response?.data?.details;
      if (details && Array.isArray(details)) {
        toast.error(`${errorMsg}: ${details.join(', ')}`);
      } else {
        toast.error(errorMsg);
      }
    },
  });

  // Delete field mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => fieldAPI.delete(selectedEvent, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fields'] });
      queryClient.invalidateQueries({ queryKey: ['registration-config'] });
      toast.success('Field deleted successfully!');
      setShowDeleteModal(false);
      setSelectedField(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete field');
    },
  });

  // Reorder fields mutation
  const reorderMutation = useMutation({
    mutationFn: (fields: any) => fieldAPI.reorder(selectedEvent, fields),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['fields'] });
      await queryClient.invalidateQueries({ queryKey: ['registration-config'] });
      toast.success('Field order updated!');
    },
    onError: (error: any) => {
      console.error('Reorder error:', error.response?.data);
      const errorMsg = error.response?.data?.error || 'Failed to reorder fields';
      const details = error.response?.data?.details;
      if (details && Array.isArray(details)) {
        toast.error(`${errorMsg}: ${details.map(d => d.message).join(', ')}`);
      } else {
        toast.error(errorMsg);
      }
    },
  });

  // Toggle field visibility mutation
  const toggleVisibilityMutation = useMutation({
    mutationFn: ({ id, is_active }: any) => {
      // Use the new visibility endpoint that handles both global and event-specific fields
      return fieldAPI.updateVisibility(selectedEvent, id, { is_active });
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['fields'] });
      // Also invalidate registration-config for SelfRegistration component
      queryClient.invalidateQueries({ queryKey: ['registration-config'] });
      // Use the message from the backend which indicates the scope
      toast.success(response.data.message);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update field visibility');
    },
  });

  const handleSaveField = (data) => {
    if (selectedField) {
      updateMutation.mutate({ id: selectedField.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = () => {
    if (selectedField) {
      deleteMutation.mutate(selectedField.id);
    }
  };

  const handleAddField = () => {
    setSelectedField(null);
    setShowFieldModal(true);
  };

  const handleEditField = (field) => {
    setSelectedField(field);
    setShowFieldModal(true);
  };

  const handleToggleVisibility = (field) => {
    toggleVisibilityMutation.mutate({
      id: field.id,
      is_active: !field.is_active,
    });
  };

  // Drag and drop handlers
  const handleDragStart = (field) => {
    setDraggedField(field);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (targetField) => {
    if (!draggedField || draggedField.id === targetField.id) {
      setDraggedField(null);
      return;
    }

    // Prevent reordering while mutation is in progress
    if (reorderMutation.isPending) {
      setDraggedField(null);
      return;
    }

    const reorderedFields = [...fields];
    const draggedIndex = reorderedFields.findIndex(f => f.id === draggedField.id);
    const targetIndex = reorderedFields.findIndex(f => f.id === targetField.id);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedField(null);
      return;
    }

    // Remove dragged item and insert at target position
    const [removed] = reorderedFields.splice(draggedIndex, 1);
    reorderedFields.splice(targetIndex, 0, removed);

    // Update display_order for all fields
    const updatedFields = reorderedFields.map((field, index) => ({
      id: field.id,
      display_order: index + 1,
    }));

    console.log('Reordering fields:', updatedFields);
    reorderMutation.mutate(updatedFields);
    setDraggedField(null);
  };

  // Auto-select first event
  React.useEffect(() => {
    if (eventsData?.events?.length > 0 && !selectedEvent) {
      setSelectedEvent(eventsData.events[0].id);
    }
  }, [eventsData, selectedEvent, setSelectedEvent]);

  // const events = eventsData?.events || []; // Commented out unused variable
  const fields = deduplicateFields(fieldsData?.custom_fields || []);

  return (
    <>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Custom Fields</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">Configure registration form fields</p>
          </div>
          <Button
            onClick={handleAddField}
            className="gap-2 w-full sm:w-auto"
            disabled={!selectedEvent}
          >
            <Plus className="h-5 w-5" />
            <span className="hidden xs:inline">Add Field</span>
            <span className="xs:hidden">Add</span>
          </Button>
        </div>

        {/* Event Selector */}
        <EventSelector 
          selectedEvent={selectedEvent}
          onEventChange={setSelectedEvent}
          className="w-full max-w-full sm:max-w-md"
          attendeeCount={fields.length}
          eventsWithCounts={eventsData?.events || []}
        />

        {/* Field count display */}
        {selectedEvent && fields.length > 0 && (
          <div className="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg w-full sm:max-w-md">
            <span className="font-medium">{fields.length}</span> unique field{fields.length !== 1 ? 's' : ''} configured
            {fieldsData?.custom_fields && fieldsData.custom_fields.length > fields.length && (
              <span className="block sm:inline text-xs text-gray-500 sm:ml-2 mt-1 sm:mt-0">
                ({fieldsData.custom_fields.length} total, duplicates removed)
              </span>
            )}
          </div>
        )}

        {/* Fields List */}
        {isLoading ? (
          <Loading text="Loading fields..." />
        ) : selectedEvent ? (
          <Card>
            <Card.Content>
              {fields.length > 0 ? (
                <div className="space-y-2">
                  <div className="mb-4 space-y-2">
                    <p className="text-sm text-gray-600 hidden sm:block">
                      <GripVertical className="h-4 w-4 inline mr-1" />
                      Drag and drop to reorder fields
                    </p>
                    <p className="text-sm text-gray-600 sm:hidden">
                      Tap to edit fields
                    </p>
                    <div className="space-y-2">
                      <div className="bg-blue-50 text-blue-700 p-3 rounded-lg text-sm mb-4 border border-blue-100 flex items-start gap-2">
                        <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-500" />
                        <div>
                          <strong>Tip:</strong> Use the eye icon to hide/show any field in registration forms, including core fields like Full Name. Hidden fields remain in this list so you can easily show them again later.
                        </div>
                      </div>
                      <div className="bg-yellow-50 text-yellow-800 p-3 rounded-lg text-sm mb-6 border border-yellow-200 flex items-start gap-2">
                        <Globe className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-600" />
                        <div>
                          <strong>Global vs Event Fields:</strong> Global fields (green badge) can be hidden/shown per event. Event-specific fields only exist for this event.
                        </div>
                      </div>
                    </div>
                  </div>
                  {fields.map((field) => (
                    <div
                      key={field.id}
                      draggable={field.is_active}
                      onDragStart={() => field.is_active && handleDragStart(field)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(field)}
                      className={`flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 border rounded-lg transition-colors ${
                        !field.is_active 
                          ? 'border-gray-200 bg-gray-50 opacity-60' 
                          : 'border-gray-200 hover:bg-gray-50'
                      } ${
                        draggedField?.id === field.id ? 'opacity-50' : ''
                      } ${
                        field.is_active ? 'sm:cursor-move' : 'cursor-default'
                      }`}
                    >
                      <div className="flex items-center gap-3 sm:gap-4 flex-1">
                        <GripVertical className="h-5 w-5 text-gray-400 hidden sm:block" />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <h3 className={`font-medium text-base sm:text-sm break-words ${field.is_active ? 'text-gray-900' : 'text-gray-500'}`}>
                              {field.field_label}
                            </h3>
                            {/* Status indicators */}
                            <div className="flex flex-wrap gap-1">
                              {!field.is_active && (
                                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full font-medium">
                                  Hidden
                                </span>
                              )}
                              {/* Default field indicator */}
                              {['full_name', 'email', 'phone', 'organization', 'position'].includes(field.field_name) && (
                                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full font-semibold">
                                  Default
                                </span>
                              )}
                              {field.is_global && (
                                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full font-semibold">
                                  Global
                                </span>
                              )}
                              {field.is_required && field.is_active && (
                                <span className="text-xs px-2 py-0.5 bg-red-100 text-red-800 rounded-full">
                                  Required
                                </span>
                              )}
                              {field.show_on_badge && field.is_active && (
                                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">
                                  On Badge
                                </span>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 mt-1 break-all">
                            <span className="hidden sm:inline">Type: {field.field_type} • Name: {field.field_name}</span>
                            <span className="sm:hidden">{field.field_type}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-1 sm:gap-2 flex-shrink-0">
                        {/* Visibility toggle — available for ALL fields including core ones */}
                        <button
                          onClick={() => handleToggleVisibility(field)}
                          className={`p-2 rounded transition-colors ${
                            field.is_active
                              ? 'text-green-600 hover:bg-green-50 hover:text-green-700'
                              : 'text-orange-500 hover:bg-orange-50 hover:text-orange-600'
                          }`}
                          title={
                            field.is_global
                              ? (field.is_active
                                  ? 'Hide this global field for this event only'
                                  : 'Show this global field for this event only')
                              : (field.is_active
                                  ? 'Hide from registration form for this event'
                                  : 'Show in registration form for this event')
                          }
                          disabled={toggleVisibilityMutation.isPending}
                        >
                          {field.is_active ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <EyeOff className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleEditField(field)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Edit field"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedField(field);
                            setShowDeleteModal(true);
                          }}
                          className={`p-2 rounded transition-colors ${
                            ['full_name', 'email', 'phone', 'organization', 'position'].includes(field.field_name)
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-red-600 hover:bg-red-50'
                          }`}
                          title={
                            ['full_name', 'email', 'phone', 'organization', 'position'].includes(field.field_name)
                              ? 'Default fields cannot be deleted'
                              : 'Delete field'
                          }
                          disabled={['full_name', 'email', 'phone', 'organization', 'position'].includes(field.field_name)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500 mb-4">No fields configured for this event</p>
                  <Button onClick={handleAddField} className="gap-2">
                    <Plus className="h-5 w-5" />
                    Add First Field
                  </Button>
                </div>
              )}
            </Card.Content>
          </Card>
        ) : (
          <Card>
            <Card.Content className="text-center py-12">
              <p className="text-gray-500">Select an event to manage its fields</p>
            </Card.Content>
          </Card>
        )}

        {/* Field Form Modal */}
        <FieldFormModal
          isOpen={showFieldModal}
          onClose={() => {
            setShowFieldModal(false);
            setSelectedField(null);
          }}
          onSave={handleSaveField}
          field={selectedField}
          loading={createMutation.isPending || updateMutation.isPending}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedField(null);
          }}
          onConfirm={handleDelete}
          title="Delete Field"
          message={`Are you sure you want to delete "${selectedField?.field_label}"? This will affect all attendees.`}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          loading={deleteMutation.isPending}
        />
      </div>
    </>
  );
};

export default CustomFields;
